import { createAsyncThunk } from '@reduxjs/toolkit';

import { ApiResListsType, OhsApiRequestName } from 'global-services/api/OhsApiModels';
import globalModuleSearch from 'search/OhsSearchServices';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';
import { OhsQrCodeListsFilterPayload, OhsQrCodeRootState } from 'qr-code/models/OhsQrCodeModels';
import { getQrCodeAllocatedList, getQrCodeLists } from 'qr-code/OhsQrCodeServices';
import OhsQrCodeRecord from 'qr-code/models/OhsQrCodeRecord';
import { TierType } from 'global-services/constants/OhsObject';
import parseListAllocations from 'qr-code/utils/OhsParseAllocationList';

export const qrCodeSearch = async (
  searchDetails: any,
  apiRequestName?: OhsApiRequestName
): Promise<ApiResListsType<any[]> | null> => {
  const globalSearchRes: any = await globalModuleSearch(searchDetails, apiRequestName);
  return globalSearchRes.result;
};

export const getQrCodeWithAllocations = (
  state: OhsQrCodeRootState,
  filterInfo: OhsQrCodeListsFilterPayload
): Promise<ApiResListsType<any[]> | null> => {
  const { globalSearch } = state;
  const hasGlobalSearch = globalSearch.searchInfo.searchKey !== '';
  const getSearchFilter = () => {
    const { filter } = globalSearch.searchInfo;
    return { ...globalSearch.searchInfo, filter: { ...filter, count: false } };
  };
  const getQrCodeListsReq = async (): Promise<ApiResListsType<any[]> | null> => {
    const qrCodeLists = await (hasGlobalSearch
      ? qrCodeSearch(getSearchFilter())
      : getQrCodeLists(filterInfo));

    if (qrCodeLists && qrCodeLists?.items.length === 0) {
      return new Promise((resolve) => {
        resolve(qrCodeLists);
      });
    }
    // only check tier3 records
    const getIDs: string[] = [];
    qrCodeLists?.items.forEach((item) => {
      if (item._id && item.tier.type === TierType.T3) getIDs.push(item._id);
    });
    if (getIDs && getIDs.length === 0) {
      return new Promise((resolve) => {
        resolve(qrCodeLists);
      });
    }

    const allocatedList = await getQrCodeAllocatedList(getIDs);

    const qrCodeListWithAllocations = parseListAllocations(
      qrCodeLists,
      allocatedList
    ) as ApiResListsType<OhsQrCodeRecord[]> | null;

    return new Promise((resolve) => {
      resolve(qrCodeListWithAllocations);
    });
  };

  return getQrCodeListsReq();
};

export const qrCodeListsRPC = async (
  qrCodeState: OhsQrCodeRootState,
  qrCodeFilters: OhsQrCodeListsFilterPayload,
  count: boolean
): Promise<ApiResListsType<OhsQrCodeRecord[]> | null> => {
  const { globalSearch } = qrCodeState;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;
  const hasGlobalSearch = qrCodeState.globalSearch.searchInfo.searchKey !== '';

  const getSearchFilter = () => {
    const { filter } = globalSearch.searchInfo;
    return { ...globalSearch.searchInfo, filter: { ...filter, count } };
  };

  if (userTier && userTier === 4 && user?.configs.qrcode?.PERMISSIONS.view) {
    const response = hasGlobalSearch
      ? await qrCodeSearch(getSearchFilter())
      : await getQrCodeLists(qrCodeFilters);
    return response;
  }
  if (userTier && userTier === 3 && user?.configs.qrcode?.PERMISSIONS.view) {
    const response = hasGlobalSearch
      ? await qrCodeSearch(getSearchFilter())
      : await getQrCodeWithAllocations(qrCodeState, qrCodeFilters);
    return response;
  }

  return null;
};

export const fetchQrCodeListsCountAsync = createAsyncThunk<
  ApiResListsType<OhsQrCodeRecord[]> | null,
  undefined
>('qrCode/fetchQrCodeListsCount', async (_, thunkAPI) => {
  const {
    qrCode,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsQrCodeRootState;
  const { currentViewPreset } = qrCode;
  const viewPresetFilter = filterInfo.qrCodeModule[currentViewPreset];
  const qrCodeState = thunkAPI.getState() as OhsQrCodeRootState;
  const qrCodeFiltersWithCount: OhsQrCodeListsFilterPayload = {
    ...viewPresetFilter,
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.qrCodeModule.sort)) },
  };

  try {
    const response = qrCodeListsRPC(qrCodeState, qrCodeFiltersWithCount, true);
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchQrCodeListsAsync = createAsyncThunk<
  ApiResListsType<OhsQrCodeRecord[]> | null,
  undefined
>('qrCode/fetchQrCodeLists', async (_, thunkAPI) => {
  const {
    qrCode,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsQrCodeRootState;

  const qrCodeState = thunkAPI.getState() as OhsQrCodeRootState;
  const data = qrCode.qrCodeLists;
  const { currentPage, currentViewPreset } = qrCode;
  const viewPresetFilter = filterInfo.qrCodeModule[currentViewPreset];

  const genQrCodeFilters: OhsQrCodeListsFilterPayload = {
    ...viewPresetFilter,
    next: currentPage > 10 ? data?.pagination.next ?? '' : undefined,
    // remove page if it is greater than 10
    ...(currentPage <= 10 && { page: currentPage }),
    sort: { ...JSON.parse(String(filterInfo.qrCodeModule.sort)) },
    count: false,
  };

  try {
    const response = qrCodeListsRPC(qrCodeState, genQrCodeFilters, false);
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
